import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthShell from '../../components/marketing/AuthShell';
import {
  useResetPassword, useSendForgotPasswordOtp, useVerifyForgotPasswordOtp,
} from '../../api/hooks';
import { plainError } from '../../lib/format';

/**
 * Setting a password when you cannot sign in.
 *
 * Two routes to the same place, because the backend offers two and they do not
 * work the same way:
 *
 *   EMAIL — the server sends a reset LINK. Nothing more happens on this
 *           screen; the token arrives in the URL of that mail and the reset
 *           finishes on /reset-password. So the email path ends at "go and
 *           check your inbox", and saying anything else would be a lie.
 *   SMS   — the server texts a one-time code. That code is exchanged here for
 *           a reset token, and the new password is set without leaving.
 *
 * It is also how a NEW MEMBER OF STAFF gets their first password: the console
 * creates their account with no usable one, by design, and this is the flow
 * the backend expects them to use. Hence the note at the bottom — somebody
 * arriving here from a "you have been added" message needs to know they are
 * in the right place.
 */
const MIN_PASSWORD = 8;

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const [method, setMethod] = useState('email');
  const [email, setEmail] = useState(params.get('email') || '');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  /* ask → the form; sent → an email is on its way; code → the OTP and the new
     password; done → finished, go and sign in. */
  const [stage, setStage] = useState('ask');
  const [note, setNote] = useState(null);
  const [failure, setFailure] = useState(null);

  const [send, sendState] = useSendForgotPasswordOtp();
  const [verify, verifyState] = useVerifyForgotPasswordOtp();
  const [reset, resetState] = useResetPassword();

  const start = async (e) => {
    e.preventDefault();
    setFailure(null);
    setNote(null);
    try {
      const { data } = await send({ variables: { input: { email: email.trim(), method } } });
      const r = data?.sendForgotPasswordOtp;
      if (!r?.success) throw new Error(r?.message || 'That did not work');
      setNote(r.message);
      setStage(method === 'email' ? 'sent' : 'code');
    } catch (err) {
      setFailure(plainError(err, 'We could not start the reset'));
    }
  };

  const finish = async (e) => {
    e.preventDefault();
    setFailure(null);
    if (password.length < MIN_PASSWORD) {
      setFailure(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setFailure('The two passwords do not match.');
      return;
    }
    try {
      /* The code is worth a token, and the token is what resets. */
      const { data } = await verify({ variables: { input: { email: email.trim(), otp: otp.trim() } } });
      const v = data?.verifyForgotPasswordOtp;
      if (!v?.success || !v.resetToken) throw new Error(v?.message || 'That code was not accepted');

      const { data: done } = await reset({
        variables: { input: { token: v.resetToken, newPassword: password } },
      });
      const r = done?.resetPassword;
      if (!r?.success) throw new Error(r?.message || 'The password was not changed');
      setNote(r.message || 'Your password is set.');
      setStage('done');
    } catch (err) {
      setFailure(plainError(err, 'We could not set the password'));
    }
  };

  const busy = sendState.loading || verifyState.loading || resetState.loading;

  return (
    <AuthShell
      aside={(
        <>
          <div>
            <h2>Locked out, or never had a password?</h2>
            <p className="read">
              Both are the same thing to fix. We will send you a link by email, or a code by text, and you
              choose a new password.
            </p>
          </div>
          <span style={{ fontSize: 12, color: '#8b90a8' }}>© 2026 Arthaprajna Private Limited</span>
        </>
      )}
    >
      <h1>Reset your password</h1>

      {stage === 'ask' && (
        <>
          <div className="sub">
            Tell us the address on the account. An email gets you a reset link; a text gets you a code you can
            use right here.
          </div>
          <form onSubmit={start}>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                autoComplete="email"
                placeholder="you@yourfirm.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label>How should we reach you?</label>
              <select className="fin" value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="email">Email me a reset link</option>
                <option value="sms">Text me a code</option>
              </select>
            </div>
            <button className="btn pri lg" style={{ width: '100%', marginTop: 22 }} type="submit" disabled={busy}>
              {busy ? 'Please wait…' : method === 'email' ? 'Send the link →' : 'Send the code →'}
            </button>
            {failure && <div className="auth-msg bad">{failure}</div>}
          </form>
        </>
      )}

      {stage === 'sent' && (
        <>
          <div className="auth-msg">{note || 'Check your email for the reset link.'}</div>
          <div className="sub" style={{ marginTop: 14 }}>
            The link takes you to a page where you choose the new password. It is only good for a short while,
            so if it has gone stale just ask for another.
          </div>
          <div className="sub" style={{ marginTop: 10 }}>
            If the link opens something that is not this app, copy it out of the address bar and paste it on the
            <Link to="/reset-password" style={{ color: 'var(--brand)', fontWeight: 700 }}> reset page</Link> —
            the token in it still works.
          </div>
          <button className="btn" style={{ width: '100%', marginTop: 18 }} type="button" onClick={() => setStage('ask')}>
            Send another
          </button>
        </>
      )}

      {stage === 'code' && (
        <>
          <div className="sub">{note || 'We texted you a code.'} Enter it, and pick your new password.</div>
          <form onSubmit={finish}>
            <div className="field">
              <label>The code we texted</label>
              <input
                inputMode="numeric"
                placeholder="######"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="field">
              <label>New password</label>
              <input
                type="password"
                autoComplete="new-password"
                placeholder={`At least ${MIN_PASSWORD} characters`}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="field">
              <label>New password again</label>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <button className="btn pri lg" style={{ width: '100%', marginTop: 22 }} type="submit" disabled={busy}>
              {busy ? 'Please wait…' : 'Set the password →'}
            </button>
            {failure && <div className="auth-msg bad">{failure}</div>}
          </form>
        </>
      )}

      {stage === 'done' && (
        <>
          <div className="auth-msg">{note}</div>
          <Link className="btn pri lg" style={{ width: '100%', marginTop: 18, display: 'block', textAlign: 'center' }} to="/login">
            Sign in →
          </Link>
        </>
      )}

      <p style={{ marginTop: 22, fontSize: 12, color: 'var(--ink3)', lineHeight: 1.55 }}>
        Just been added as staff? You are in the right place — new accounts are created without a password, and
        this is how you set the first one.
      </p>
      <div className="auth-alt">Remembered it? <Link to="/login">Back to sign in</Link></div>
    </AuthShell>
  );
}
